// src/CliniciansView.jsx
import React, { useEffect, useMemo, useState } from "react";

const INSURANCE_OPTIONS = [
  "Aetna",
  "BCBS",
  "Highmark",
  "Highmark Wholecare",
  "United",
  "Medicaid",
  "Medicare",
  "Other (see notes)",
  "Self-Pay",
  "No insurance",
  "Sliding scale (intern)",
];

const LOCATION_OPTIONS = ["Myerstown", "Lancaster", "Middletown"];

export default function CliniciansView({ clinicians, clients, assignments, api }) {
  const [selectedId, setSelectedId] = useState(null);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    clinician_type: "mft", // mft | social_worker | intern
    insurances: ["Aetna"], // multi
    modality: "in_person", // in_person | virtual | either | hybrid
    locations: ["Myerstown"], // multi
    accepts_minors: false,
    gender: "female",
    notes: "",
    availability_notes: "",
  });

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return clinicians.find((c) => String(c.id) === String(selectedId)) || null;
  }, [clinicians, selectedId]);

  // When selected changes, exit edit mode
  useEffect(() => {
    setIsEditing(false);
    setEditDraft(null);
  }, [selectedId]);

  const assignedClientsByClinician = useMemo(() => {
    const map = new Map();
    for (const a of assignments) {
      const client = clients.find((c) => c.id === a.client_id);
      if (!client) continue;
      const arr = map.get(a.clinician_id) || [];
      arr.push(`${client.first_name} ${client.last_name}`);
      map.set(a.clinician_id, arr);
    }
    return map;
  }, [assignments, clients]);

  const toggleInsurance = (value) => {
    setForm((prev) => {
      const has = prev.insurances.includes(value);
      const next = has ? prev.insurances.filter((x) => x !== value) : [...prev.insurances, value];
      return { ...prev, insurances: next.length ? next : ["Aetna"] };
    });
  };

  const toggleLocation = (value) => {
    setForm((prev) => {
      const has = prev.locations.includes(value);
      const next = has ? prev.locations.filter((x) => x !== value) : [...prev.locations, value];
      return { ...prev, locations: next.length ? next : ["Myerstown"] };
    });
  };

  const submit = (e) => {
    e.preventDefault();

    api.addClinician({
      ...form,
      // Store as array for consistency with older data/table
      modalities: [form.modality],
      locations: form.modality === "virtual" ? ["virtual"] : form.locations,
    });

    setForm({
      first_name: "",
      last_name: "",
      clinician_type: "mft",
      insurances: ["Aetna"],
      modality: "in_person",
      locations: ["Myerstown"],
      accepts_minors: false,
      gender: "female",
      notes: "",
      availability_notes: "",
    });
  };

  const startEdit = () => {
    if (!selected) return;

    const modalities = Array.isArray(selected.modalities) ? selected.modalities : [];
    const modality = selected.modality || modalities[0] || "in_person";

    const locationsRaw = Array.isArray(selected.locations) ? selected.locations : [];
    const locations =
      modality === "virtual"
        ? ["virtual"]
        : locationsRaw.filter((x) => x !== "virtual").length
        ? locationsRaw.filter((x) => x !== "virtual")
        : ["Myerstown"];

    setIsEditing(true);
    setEditDraft({
      first_name: selected.first_name || "",
      last_name: selected.last_name || "",
      clinician_type: selected.clinician_type || "mft",
      insurances:
        Array.isArray(selected.insurances) && selected.insurances.length ? selected.insurances : ["Aetna"],
      modality,
      locations,
      accepts_minors: !!selected.accepts_minors,
      gender: selected.gender || "female",
      notes: selected.notes || "",
      availability_notes: selected.availability_notes || "",
    });
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditDraft(null);
  };

  const saveEdit = () => {
    if (!selected || !editDraft) return;

    api.updateClinician(selected.id, {
      ...editDraft,
      modalities: [editDraft.modality],
      locations: editDraft.modality === "virtual" ? ["virtual"] : editDraft.locations,
    });

    setIsEditing(false);
    setEditDraft(null);
  };

  const deleteSelected = () => {
    if (!selected) return;
    const ok = window.confirm(
      `Delete ${selected.first_name} ${selected.last_name}? This cannot be undone.`
    );
    if (!ok) return;
    api.deleteClinician(selected.id);
    setSelectedId(null);
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Add Clinician */}
      <div className="cp-card alt">
        <h3 className="cp-h3" style={{ marginBottom: 12 }}>
          Add Clinician
        </h3>

        <form onSubmit={submit}>
          <div className="cp-form">
            <Field label="First name">
              <input
                className="cp-input"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </Field>

            <Field label="Last name">
              <input
                className="cp-input"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
              />
            </Field>

            <Field label="Track / role">
              <select
                className="cp-select"
                value={form.clinician_type}
                onChange={(e) => setForm({ ...form, clinician_type: e.target.value })}
              >
                <option value="mft">Marriage &amp; Family Therapist</option>
                <option value="social_worker">Social Worker</option>
                <option value="intern">Intern</option>
              </select>
            </Field>

            <Field label="Accepts minors">
              <select
                className="cp-select"
                value={form.accepts_minors ? "yes" : "no"}
                onChange={(e) => setForm({ ...form, accepts_minors: e.target.value === "yes" })}
              >
                <option value="no">no</option>
                <option value="yes">yes</option>
              </select>
            </Field>

            <Field label="Gender">
              <select
                className="cp-select"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
              >
                <option value="female">female</option>
                <option value="male">male</option>
                <option value="nonbinary">nonbinary</option>
              </select>
            </Field>

            <Field label="Modality">
              <select
                className="cp-select"
                value={form.modality}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    modality: v,
                    locations:
                      v === "virtual"
                        ? ["virtual"]
                        : prev.locations.includes("virtual")
                        ? ["Myerstown"]
                        : prev.locations.length
                        ? prev.locations
                        : ["Myerstown"],
                  }));
                }}
              >
                <option value="in_person">in-person</option>
                <option value="virtual">virtual</option>
                <option value="either">either</option>
                <option value="hybrid">hybrid</option>
              </select>
            </Field>
          </div>

          {/* Locations (multi) */}
          <div className="cp-section">
            <div className="cp-sectionPanel">
              <div className="cp-sectionTitle">Locations</div>

              <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                {LOCATION_OPTIONS.map((loc) => {
                  const disabled = form.modality === "virtual";
                  const checked = disabled ? false : form.locations.includes(loc);

                  return (
                    <label
                      key={loc}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontWeight: 800,
                        color: "var(--text)",
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleLocation(loc)}
                        style={{ width: 18, height: 18 }}
                      />
                      {loc}
                    </label>
                  );
                })}

                {form.modality === "virtual" && (
                  <div style={{ fontWeight: 800, color: "var(--muted)" }}>
                    Virtual selected — location is set to “virtual”.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Insurance (multi) */}
          <div className="cp-section">
            <div className="cp-sectionPanel">
              <div className="cp-sectionTitle">Insurance / payment accepted</div>

              <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                {INSURANCE_OPTIONS.map((opt) => {
                  const checked = form.insurances.includes(opt);
                  return (
                    <label
                      key={opt}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        fontWeight: 800,
                        color: "var(--text)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleInsurance(opt)}
                        style={{ width: 18, height: 18 }}
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="cp-section">
            <div className="cp-sectionPanel">
              <div className="cp-sectionTitle">Specialty / Notes (long)</div>
              <textarea
                className="cp-textarea"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <div className="cp-section">
            <div className="cp-sectionPanel">
              <div className="cp-sectionTitle">Availability (typed)</div>
              <textarea
                className="cp-textarea"
                value={form.availability_notes}
                onChange={(e) => setForm({ ...form, availability_notes: e.target.value })}
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <button className="cp-btn primary" type="submit">
              Add clinician
            </button>
          </div>
        </form>
      </div>

      {/* Selected Clinician Panel */}
      {selected && (
        <div className="cp-card">
          <div className="cp-row" style={{ alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <h3 className="cp-h3">
                {selected.first_name} {selected.last_name}
              </h3>

              <div className="cp-mutedSmall" style={{ marginTop: 4, fontWeight: 900, color: "var(--muted)" }}>
                Created {fmtDate(selected.created_at)} • Updated {fmtDate(selected.updated_at || selected.created_at)}
              </div>
            </div>

            <div className="cp-actions">
              {!isEditing ? (
                <button type="button" className="cp-btn" onClick={startEdit}>
                  Edit
                </button>
              ) : (
                <>
                  <button type="button" className="cp-btn primary" onClick={saveEdit}>
                    Save
                  </button>
                  <button type="button" className="cp-btn" onClick={cancelEdit}>
                    Cancel
                  </button>
                </>
              )}

              <button type="button" className="cp-btn danger" onClick={deleteSelected}>
                Delete
              </button>

              <button type="button" className="cp-btn" onClick={() => setSelectedId(null)}>
                Close
              </button>
            </div>
          </div>

          {isEditing && editDraft && (
            <div className="cp-section" style={{ marginTop: 14 }}>
              <div className="cp-sectionPanel">
                <div className="cp-sectionTitle">Edit clinician details</div>

                <div className="cp-form" style={{ marginTop: 10 }}>
                  <Field label="First name">
                    <input
                      className="cp-input"
                      value={editDraft.first_name}
                      onChange={(e) => setEditDraft({ ...editDraft, first_name: e.target.value })}
                    />
                  </Field>

                  <Field label="Last name">
                    <input
                      className="cp-input"
                      value={editDraft.last_name}
                      onChange={(e) => setEditDraft({ ...editDraft, last_name: e.target.value })}
                    />
                  </Field>

                  <Field label="Track / role">
                    <select
                      className="cp-select"
                      value={editDraft.clinician_type}
                      onChange={(e) => setEditDraft({ ...editDraft, clinician_type: e.target.value })}
                    >
                      <option value="mft">Marriage &amp; Family Therapist</option>
                      <option value="social_worker">Social Worker</option>
                      <option value="intern">Intern</option>
                    </select>
                  </Field>

                  <Field label="Accepts minors">
                    <select
                      className="cp-select"
                      value={editDraft.accepts_minors ? "yes" : "no"}
                      onChange={(e) => setEditDraft({ ...editDraft, accepts_minors: e.target.value === "yes" })}
                    >
                      <option value="no">no</option>
                      <option value="yes">yes</option>
                    </select>
                  </Field>

                  <Field label="Gender">
                    <select
                      className="cp-select"
                      value={editDraft.gender}
                      onChange={(e) => setEditDraft({ ...editDraft, gender: e.target.value })}
                    >
                      <option value="female">female</option>
                      <option value="male">male</option>
                      <option value="nonbinary">nonbinary</option>
                    </select>
                  </Field>

                  <Field label="Modality">
                    <select
                      className="cp-select"
                      value={editDraft.modality}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditDraft((prev) => ({
                          ...prev,
                          modality: v,
                          locations:
                            v === "virtual"
                              ? ["virtual"]
                              : prev.locations.includes("virtual")
                              ? ["Myerstown"]
                              : prev.locations.length
                              ? prev.locations
                              : ["Myerstown"],
                        }));
                      }}
                    >
                      <option value="in_person">in-person</option>
                      <option value="virtual">virtual</option>
                      <option value="either">either</option>
                      <option value="hybrid">hybrid</option>
                    </select>
                  </Field>
                </div>

                {/* Locations (multi) */}
                <div className="cp-section" style={{ marginTop: 12 }}>
                  <div className="cp-sectionTitle">Locations</div>

                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    {LOCATION_OPTIONS.map((loc) => {
                      const disabled = editDraft.modality === "virtual";
                      const checked = disabled ? false : editDraft.locations.includes(loc);

                      return (
                        <label
                          key={loc}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            fontWeight: 800,
                            color: "var(--text)",
                            opacity: disabled ? 0.6 : 1,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => {
                              setEditDraft((prev) => {
                                const has = prev.locations.includes(loc);
                                const next = has
                                  ? prev.locations.filter((x) => x !== loc)
                                  : [...prev.locations, loc];
                                return { ...prev, locations: next.length ? next : ["Myerstown"] };
                              });
                            }}
                            style={{ width: 18, height: 18 }}
                          />
                          {loc}
                        </label>
                      );
                    })}

                    {editDraft.modality === "virtual" && (
                      <div style={{ fontWeight: 800, color: "var(--muted)" }}>
                        Virtual selected — location is set to “virtual”.
                      </div>
                    )}
                  </div>
                </div>

                {/* Insurance (multi) */}
                <div className="cp-section" style={{ marginTop: 12 }}>
                  <div className="cp-sectionTitle">Insurance / payment accepted</div>

                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    {INSURANCE_OPTIONS.map((opt) => {
                      const checked = editDraft.insurances.includes(opt);
                      return (
                        <label
                          key={opt}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            fontWeight: 800,
                            color: "var(--text)",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setEditDraft((prev) => {
                                const has = prev.insurances.includes(opt);
                                const next = has
                                  ? prev.insurances.filter((x) => x !== opt)
                                  : [...prev.insurances, opt];
                                return { ...prev, insurances: next.length ? next : ["Aetna"] };
                              });
                            }}
                            style={{ width: 18, height: 18 }}
                          />
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="cp-section" style={{ marginTop: 12 }}>
                  <div className="cp-sectionTitle">Specialty / Notes</div>
                  <textarea
                    className="cp-textarea"
                    value={editDraft.notes}
                    onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                  />
                </div>

                <div className="cp-section" style={{ marginTop: 12 }}>
                  <div className="cp-sectionTitle">Availability</div>
                  <textarea
                    className="cp-textarea"
                    value={editDraft.availability_notes}
                    onChange={(e) => setEditDraft({ ...editDraft, availability_notes: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clinicians Table */}
      <div className="cp-card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <h3 className="cp-h3">Clinicians</h3>
          <div className="cp-mutedSmall" style={{ fontWeight: 800, color: "var(--muted)" }}>
            {clinicians.length} total
          </div>
        </div>

        <div className="cp-tableWrap" style={{ marginTop: 10, overflowX: "auto" }}>
          <table className="cp-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Track</th>
                <th>Insurances</th>
                <th>Modality</th>
                <th>Locations</th>
                <th>Assigned clients</th>
              </tr>
            </thead>
            <tbody>
              {clinicians.map((c) => {
                const assigned = assignedClientsByClinician.get(c.id) || [];
                const modalities = c.modalities || [];
                const locations = c.locations || [];
                const isSelected = String(c.id) === String(selectedId);

                return (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    style={{
                      cursor: "pointer",
                      background: isSelected ? "rgba(29,78,216,.08)" : "transparent",
                    }}
                  >
                    <td style={{ fontWeight: 900 }}>
                      {c.first_name} {c.last_name}
                    </td>
                    <td>{prettyTrack(c.clinician_type)}</td>
                    <td>{(c.insurances || []).join(", ") || "—"}</td>
                    <td>{modalities.map(prettyModality).join(", ")}</td>
                    <td>{locations.join(", ")}</td>
                    <td>{assigned.length ? assigned.join(", ") : "—"}</td>
                  </tr>
                );
              })}

              {clinicians.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 12, fontWeight: 800, color: "var(--muted)" }}>
                    No clinicians yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: "var(--muted)" }}>{label}</div>
      {children}
    </div>
  );
}

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleDateString();
  } catch {
    return "—";
  }
}

function prettyModality(v) {
  return (
    {
      in_person: "In-person",
      virtual: "Virtual",
      either: "Either",
      hybrid: "Hybrid",
    }[v] || v
  );
}

function prettyTrack(v) {
  return (
    {
      mft: "Marriage & Family Therapist",
      social_worker: "Social Worker",
      intern: "Intern",
    }[v] || v
  );
}
